import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSession } from '../Utils/SessionContext';
import AddJobPopup from '../JobComponents/AddJobPopup';
import { API_ENDPOINTS } from '../Utils/ApiConfig';
import Navigation from './Components/Navigation';
import JobStatus from '../JobComponents/Utils/JobStatus';

const Jobs = () => {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isAddJobPopupOpen, setIsAddJobPopupOpen] = useState(false);

  
  // Set document title
  useEffect(() => {
    document.title = "Jobs";
  }, []);
  
  // Pagination state
  const [pageSize] = useState(20); // Number of items per page
  const [currentOffset, setCurrentOffset] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [isLastPage, setIsLastPage] = useState(false);
  
  const navigate = useNavigate();
  const location = useLocation();
  const { logout, user } = useSession();

  // Function to update URL with current pagination parameters
  const updateURL = (offset, page, replace = false) => {
    const urlParams = new URLSearchParams();
    
    if (offset > 0) {
      urlParams.set('o', offset.toString());
    }
    if (page > 1) {
      urlParams.set('p', page.toString());
    }
    
    const newUrl = urlParams.toString() ? `${location.pathname}?${urlParams.toString()}` : location.pathname;
    navigate(newUrl, { replace });
  };

  // Function to read URL parameters and set state
  const readURLParams = () => {
    const urlParams = new URLSearchParams(location.search);
    
    const offset = parseInt(urlParams.get('o') || '0', 10);
    const page = parseInt(urlParams.get('p') || '1', 10);
    
    setCurrentOffset(offset);
    setCurrentPage(page);
    
    return { offset, page };
  };

  // Function to fetch jobs from the API
  const fetchJobs = async (offset = 0) => {
    try {
      setLoading(true);
      
      // Check if user exists
      if (!user) {
        throw new Error('User session not available. Please log in again.');
      }
      
      // Make request with authorization header and pagination parameters as URL query parameters
      const url = new URL(API_ENDPOINTS.GET_JOBS);
      url.searchParams.append('limit', pageSize);
      url.searchParams.append('offset', offset);
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${user.access_token}`
        }
      });
      
      if (!response.ok) {
        const error = new Error(`Error: ${response.status} ${response.statusText}`);
        error.status = response.status; // Add status to the error object for use in catch block
        throw error;
      }
      
      // Parse the response data with error handling for empty responses
      let data = [];
      try {
        data = await response.json();
        console.log("jobs fetched: ", data);
      } catch (parseError) {
        console.log('Empty or invalid response received');
        data = [];
      }
      
      // Check if this is the last page (if we received fewer items than the page size)
      setIsLastPage(data.length < pageSize);
      
      setJobs(data);
      setError(null);
    } catch (err) {
      console.error('Error fetching jobs:', err);
      console.log('Error status code:', err.status);
      
      // If it's a 401 error, it's an expired session - log out and redirect to login
      if (err.status === 401) {
        console.log('Session expired or unauthorized. Redirecting to login page.');
        logout();
        navigate('/login');
        return;
      }
      
      setError(err.message || 'Failed to fetch jobs');
    } finally {
      setLoading(false);
    }
  };

  // Initial load - read URL parameters and fetch data
  useEffect(() => {
    const urlParams = readURLParams();
    fetchJobs(urlParams.offset);
  }, [location.search]);
  
  // Handle next page
  const handleNextPage = () => {
    if (!isLastPage) {
      const newOffset = currentOffset + pageSize;
      const newPage = currentPage + 1;
      setCurrentOffset(newOffset);
      setCurrentPage(newPage);
      updateURL(newOffset, newPage, false); // Create new history entry
      fetchJobs(newOffset);
    }
  };
  
  // Handle previous page
  const handlePrevPage = () => {
    if (currentOffset > 0) {
      const newOffset = Math.max(0, currentOffset - pageSize);
      const newPage = currentPage - 1;
      setCurrentOffset(newOffset);
      setCurrentPage(newPage);
      updateURL(newOffset, newPage, false); // Create new history entry
      fetchJobs(newOffset);
    }
  };


  // Format date and time to a more readable format
  const formatDateTime = (dateString) => {
    const options = { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true
    };
    return new Date(dateString).toLocaleString(undefined, options);
  };

  const handleAddJobClick = () => {
    setIsAddJobPopupOpen(true);
  };

  const handleAddJobClose = () => {
    setIsAddJobPopupOpen(false);
  };

  const handleAddJobSubmit = async (formData) => {
    // Create a FormData object to send the file, link, extra info, and user ID
    const formDataToSend = new FormData();
    formDataToSend.append('jobDescriptionFile', formData.jobDescriptionFile);
    if (formData.companyLink) {
      formDataToSend.append('companyLink', formData.companyLink);
    }
    if (formData.extraInfo) {
      formDataToSend.append('extraInfo', formData.extraInfo);
    }
    // Add user ID from session
    if (user && user.id) {
      formDataToSend.append('uid', user.id);
    }
    
    // Add API testing fields
    if (formData.apikey) {
      formDataToSend.append('apikey', formData.apikey);
    }
    if (formData.callback) {
      formDataToSend.append('callback', formData.callback);
    }
    if (formData.callbackEmail) {
      formDataToSend.append('callbackEmail', formData.callbackEmail);
    }
    if (formData.internalID) {
      formDataToSend.append('internalID', formData.internalID);
    }
    formDataToSend.append('generateVsJobEvaluation', formData.generateVsJobEvaluation);
    
    let response;
    
    // Check if API key is provided in the form
    if (formData.apikey) {
      // If API key is provided, use NEW_JOB_API without authentication headers
      console.log('Using API key authentication');
      response = await fetch(API_ENDPOINTS.NEW_JOB_API, {
        method: 'POST',
        body: formDataToSend
      });
    } else {
      // If no API key is provided, use NEW_JOB with authentication headers
      console.log('Using token authentication');
      response = await fetch(API_ENDPOINTS.NEW_JOB, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${user.access_token}`
        },
        body: formDataToSend
      });
    }
    
    if (!response.ok) {
      const error = new Error(`Error: ${response.status} ${response.statusText}`);
      error.status = response.status; // Add status to the error object for use in catch block
      throw error;
    }
    
    // Parse the response
    const data = await response.json();
    console.log('Job created successfully:', data);
    
    // Refresh the jobs list - reset to first page when new job is added
    setCurrentOffset(0);
    setCurrentPage(1);
    fetchJobs(0);
  };

  return (
    <div className="min-h-screen bg-gray-100 p-5 pt-[76px]"> {/* Added top padding to account for fixed header */}
      <Navigation title="Jobs" showBackButton={false} />
      
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex justify-between items-center border-b border-gray-200 pb-3 mb-4">
          <h2 className="text-xl font-semibold text-gray-800">
            Available Jobs
          </h2>
          <button 
            onClick={handleAddJobClick}
            className="px-4 py-2 bg-blue-500 text-white font-semibold rounded hover:bg-blue-600 transition-colors flex items-center"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path>
            </svg>
            Add New Job
          </button>
        </div>
        
        {loading ? (
          <div className="text-center py-8">
            <p className="text-gray-600">Loading jobs...</p>
          </div>
        ) : error ? (
          <div className="text-center py-8">
            <p className="text-red-500">{error}</p>
            <p className="text-gray-600 mt-2">Please try again or refresh the page.</p>
          </div>
        ) : jobs.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-gray-600">No jobs available at the moment.</p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Job Name
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Created Date
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Candidates
                  </th>
                  <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {jobs.map((job) => (
                  <tr 
                    key={job.job_id} 
                    className="hover:bg-gray-50 cursor-pointer"
                    onClick={() => navigate(`/job-details?jid=${job.job_id}`)}
                  >
                    <td className="px-6 py-4 whitespace-nowrap text-base font-medium text-gray-900">
                      <div>
                        <p className="font-bold">{job.job_name || 'No Job Title'}</p>
                        <p className="text-sm text-gray-600">{job.company_name || 'Unknown Company'}</p>
                        <p className="text-xs text-gray-500">ID: {job.job_id}</p>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatDateTime(job.created_at)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      <JobStatus status={job.status} showDot={true} />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      <span className="px-3 py-1 inline-flex text-sm leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">
                        {job.candidates}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" onClick={(e) => e.stopPropagation()}>
                      <button 
                        className="p-2 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors"
                        title="Delete"
                        onClick={(e) => {
                          e.stopPropagation(); // Prevent row click event
                          
                          // Show confirmation dialog
                          if (window.confirm(`Are you sure you want to delete job "${job.job_name || 'No Job Title'}"?`)) {
                            // User confirmed, send delete request
                            const deleteJob = async () => {
                              try {
                                // Create URL with job ID parameter
                                const url = new URL(API_ENDPOINTS.DELETE_JOB);
                                url.searchParams.append('jid', job.job_id);
                                
                                const response = await fetch(url, {
                                  method: 'GET',
                                  headers: {
                                    'Authorization': `Bearer ${user.access_token}`
                                  }
                                });
                                
                                if (!response.ok) {
                                  throw new Error(`Error: ${response.status} ${response.statusText}`);
                                }
                                
                                // Remove the deleted job from the list
                                setJobs(prevJobs => 
                                  prevJobs.filter(item => item.job_id !== job.job_id)
                                );
                              } catch (err) {
                                console.error('Error deleting job:', err);
                                alert('Failed to delete job. Please try again.');
                              }
                            };
                            
                            deleteJob();
                          }
                        }}
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path>
                        </svg>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
              </table>
            </div>
            
            {/* Pagination Controls */}
            <div className="flex justify-between items-center mt-4 px-6 py-3 bg-gray-50 border-t border-gray-200">
              <div className="text-sm text-gray-700">
                Showing page {currentPage} {isLastPage ? '(last page)' : ''}
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={handlePrevPage}
                  disabled={currentOffset === 0}
                  className={`px-4 py-2 border rounded-md ${
                    currentOffset === 0 
                      ? 'bg-gray-100 text-gray-400 cursor-not-allowed' 
                      : 'bg-white text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  Previous
                </button>
                <button
                  onClick={handleNextPage}
                  disabled={isLastPage}
                  className={`px-4 py-2 border rounded-md ${
                    isLastPage 
                      ? 'bg-gray-100 text-gray-400 cursor-not-allowed' 
                      : 'bg-white text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  Next
                </button>
              </div>
            </div>
          </>
        )}
      </div>
      
      {/* Add Job Popup */}
      <AddJobPopup 
        isOpen={isAddJobPopupOpen}
        onClose={handleAddJobClose}
        onSubmit={handleAddJobSubmit}
      />
    </div>
  );
};

export default Jobs;
